import {
  setIsDragging,
  setTaskBeingDragged,
  setTaskBoxConfigurations,
  setDragConfirmed,
  createTaskBeingDraggedObject,
  cancelDrag,
} from "../../../store/dragAndDrop";
import {
  createShiftLocation,
  createUnplannedLocation,
} from "../../../service/DragAndDropApi";
import { LockAllRoutesApi } from "../../../service/LockAllRoutesApi";
import { hideTaskHoverBox, showTaskHoverBox } from "../../../store/overlay";
import { openDrawer } from "../../../store/drawer";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setHighlightedTasks } from "../../../store/search";
import {
  Employee,
  HoverTaskType,
  OperationalTask,
  WorkTask,
} from "../../../types/ganttChart";
import taskStyles from "../../../styles/ganttChart/tasks.module.css";
import { HighlightTasks } from "../../../utils/highlightUtil";
import { useEffect, useRef, useState } from "react";
import ganttChartService from "../../../service/ganttChartService";
import TaskViolationBox from "../verticalGanttChart/TaskViolationBox";
import useDragAndDropPermission from "../../../hooks/useDragAndDropPermission";
import DragAndDropIndicator from "../dragAndDrop/DragAndDropIndicator";
import DragTelemetryService from "../../../service/DragTelemetryService";
import { DragStartTrigger } from "../../../types/dragTelemetry";

const DraggableGanttChartBlock = ({
  xPosition,
  yPosition,
  width,
  defaultWidth,
  height,
  taskType,
  task,
  employee,
  borderColor,
  backgroundColor,
  children,
  allowedNumberOfLinesOfText,
  dragable,
  onClickOverride,
}: {
  xPosition: number;
  yPosition: number;
  width: number;
  defaultWidth?: number;
  height: number;
  taskType: HoverTaskType;
  task: WorkTask | OperationalTask;
  employee?: Employee;
  isDraggingMatch: boolean;
  borderColor: string;
  backgroundColor: string;
  children: React.ReactNode | React.ReactNode[];
  allowedNumberOfLinesOfText?: number;
  dragable?: boolean; //TODO: this should be removed when OperationalTask is reset to not dragable component.
  onClickOverride?: () => void;
}) => {
  // TODO LATER with claude: run through and see for updates. Also, check if this is also used nor none draggable components.
  const dispatch = useAppDispatch();
  // When active, clicking a task toggles its lockedForPlanning instead of opening the drawer.
  const isLockRoutesMode = useAppSelector(
    (state) => state.lockRoutesSlice.isLockRoutesMode,
  );

  const { isDragAndDropAllowed } = useDragAndDropPermission();
  const { pixelPerMinute } = useAppSelector((state) => state.timelineSlice);
  const { taskBeingDragged, intersectingIdTaskPlanAndPlacement, isDragging } =
    useAppSelector((state) => state.dragAndDropSlice);

  const highlightedTaskIds = useAppSelector(
    (state) => state.searchSlice.highlightedTaskIds,
  );

  // Driven by Redux (not :focus-visible) because :focus-visible doesn't match
  // pointer-triggered focus in most browsers, and this task can be "selected" via mouse
  // hover too — both cases dispatch showTaskHoverBox, so this stays in sync with either.
  const taskIsHovered = useAppSelector(
    (state) => state.overlaySlice.taskIsHovered,
  );
  const isSelected =
    taskIsHovered?.type === taskType &&
    taskIsHovered?.task.idTask === task.idTask;

  // If this task is unmounted while it's the current hover/focus target (e.g. it starts
  // being dragged, or gets remounted after a reorder) without a mouseleave/blur ever firing,
  // the stale Redux reference survives and keeps matching this task's next mounted instance —
  // showing it as permanently "selected" no matter what's actually hovered.
  const isSelectedRef = useRef(isSelected);
  isSelectedRef.current = isSelected;
  useEffect(() => {
    return () => {
      if (isSelectedRef.current) dispatch(hideTaskHoverBox());
    };
  }, [dispatch]);

  /* Below are all the calculations to draw the red violation boxes, 
  when tasks exceed the employees shift. */
  const [taskExceedsEndOfShift, setTaskExceedsEndOfShift] =
    useState<boolean>(false);
  const [violationHeight, setViolationHeight] = useState<number>();
  const [fullTaskViolation, setFullTaskViolation] = useState<boolean>(false);
  const [t, setT] = useState<Date>();
  const [tt, setTT] = useState<Date>();
  useEffect(() => {
    if (!employee) return;
    if (
      employee &&
      new Date(task.end).getTime() > new Date(employee?.shiftEndTime).getTime()
    ) {
      setT(new Date(task.end));
      setTT(new Date(employee?.shiftEndTime));
      setTaskExceedsEndOfShift(true);
      const violationHeight =
        ganttChartService.timeDifferenceInMinutes(
          employee!.shiftEndTime,
          task.end,
        ) * pixelPerMinute;

      setViolationHeight(violationHeight);
      if (violationHeight >= height) {
        setFullTaskViolation(true);
      }
    } else {
      setTaskExceedsEndOfShift(false);
      setViolationHeight(undefined);
      setFullTaskViolation(false);
    }
  }, [employee, task, pixelPerMinute, height, isDragging]);

  // State for click vs drag detection
  const mouseDownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const mouseDownTimeRef = useRef<number>(0);
  const pressAtRef = useRef<number>(0);
  const initialMousePosRef = useRef<{ x: number; y: number } | null>(null);
  const [isDragInitiated, setIsDragInitiated] = useState(false);
  const [shouldHandleClick, setShouldHandleClick] = useState(false);
  const [isTouchInteraction, setIsTouchInteraction] = useState(false);

  // More forgiving parameters for touchpad interactions
  const DRAG_DELAY_MOUSE = 400; // milliseconds for mouse
  const DRAG_DELAY_TOUCH = 1000; // longer delay for touch/touchpad
  const MOVEMENT_THRESHOLD_MOUSE = 20; // pixels for mouse
  const MOVEMENT_THRESHOLD_TOUCH = 40; // more forgiving for touch/touchpad

  const initiateDrag = (trigger: DragStartTrigger) => {
    setIsDragInitiated(true);
    dispatch(setDragConfirmed(true)); // Mark as confirmed drag
    DragTelemetryService.start({
      taskId: task.idTask,
      fromShiftId: employee ? employee.shiftSchedule.idShift : null,
      trigger,
      mouseDownAt: pressAtRef.current,
      blockHeightPx: height,
      pixelsPerMinute: pixelPerMinute,
    });

    const sourceLocation = employee
      ? createShiftLocation(employee.shiftSchedule.idShift)
      : createUnplannedLocation();
    const taskBeingDragged = createTaskBeingDraggedObject(task, sourceLocation);
    dispatch(setTaskBeingDragged(taskBeingDragged));
    dispatch(setIsDragging());
    dispatch(
      setTaskBoxConfigurations({
        height: height,
        width: defaultWidth ? defaultWidth : width,
        backgroundColor: backgroundColor,
        border: borderColor,
        allowedLines: allowedNumberOfLinesOfText
          ? allowedNumberOfLinesOfText
          : 0,
      }),
    );
  };

  const cancelCurrentDrag = () => {
    DragTelemetryService.finish("CANCELLED");
    setIsDragInitiated(false);
    dispatch(cancelDrag()); //resets all values in store.
  };

  useEffect(() => {
    //Add ECS event to calcel drag without dropping a task.
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        cancelCurrentDrag();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [cancelCurrentDrag]);

  // Mouseevents to handle drag and drop
  const handleMouseDown = (event: React.MouseEvent) => {
    if (!isDragAndDropAllowed || taskBeingDragged || !dragable) {
      setShouldHandleClick(true);
      return;
    }
    mouseDownTimeRef.current = Date.now();
    pressAtRef.current = DragTelemetryService.notePress();
    initialMousePosRef.current = { x: event.clientX, y: event.clientY };

    // Start timer for drag detection
    mouseDownTimerRef.current = setTimeout(() => {
      // After delay, initiate drag if mouse is still down

      initiateDrag("TIMER");
    }, DRAG_DELAY_MOUSE);
  };

  // Touch events to handle drag and drop
  const handleTouchStart = (event: React.TouchEvent) => {
    setIsTouchInteraction(true); // Set flag immediately for touch interactions

    if (!(isDragAndDropAllowed && !taskBeingDragged && dragable)) {
      setShouldHandleClick(true);
      return;
    }

    const touch = event.touches[0];
    // Reset states
    setIsDragInitiated(false);
    setShouldHandleClick(false);
    mouseDownTimeRef.current = Date.now();
    pressAtRef.current = DragTelemetryService.notePress("touch");
    initialMousePosRef.current = { x: touch.clientX, y: touch.clientY };

    // Start timer for drag detection with longer delay for touch
    mouseDownTimerRef.current = setTimeout(() => {
      // After delay, initiate drag if touch is still active
      initiateDrag("TIMER");
    }, DRAG_DELAY_TOUCH);
  };

  const handleMouseUp = () => {
    const mouseUpTime = Date.now();
    const timeDiff = mouseUpTime - mouseDownTimeRef.current;
    const currentDelay = isTouchInteraction
      ? DRAG_DELAY_TOUCH
      : DRAG_DELAY_MOUSE;

    // Clear the drag timer

    if (mouseDownTimerRef.current) {
      clearTimeout(mouseDownTimerRef.current);

      mouseDownTimerRef.current = null;
    }

    // If it was a quick click (under threshold) and drag wasn't initiated, handle as click
    if (timeDiff < currentDelay && !isDragInitiated) {
      setShouldHandleClick(true);
      // Reset drag confirmation state for quick clicks
      dispatch(setDragConfirmed(false));
    }

    // Reset states
    setIsDragInitiated(false);
    initialMousePosRef.current = null;
    setIsTouchInteraction(false); // Reset touch interaction flag
  };

  const handleTouchEnd = () => {
    // Same logic as handleMouseUp but ensure we're in touch mode
    setIsTouchInteraction(true);
    handleMouseUp();
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!initialMousePosRef.current) return;

    const deltaX = Math.abs(event.clientX - initialMousePosRef.current.x);
    const deltaY = Math.abs(event.clientY - initialMousePosRef.current.y);

    // If significant movement detected during hold, immediately start drag
    if (
      !isDragInitiated &&
      (deltaX > MOVEMENT_THRESHOLD_MOUSE || deltaY > MOVEMENT_THRESHOLD_MOUSE)
    ) {
      if (mouseDownTimerRef.current) {
        clearTimeout(mouseDownTimerRef.current);
        mouseDownTimerRef.current = null;
      }

      if (isDragAndDropAllowed && !taskBeingDragged && dragable) {
        initiateDrag("MOVEMENT");
      }
    }
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (!initialMousePosRef.current) return;

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - initialMousePosRef.current.x);
    const deltaY = Math.abs(touch.clientY - initialMousePosRef.current.y);

    // If significant movement detected during hold, immediately start drag
    if (
      !isDragInitiated &&
      (deltaX > MOVEMENT_THRESHOLD_TOUCH || deltaY > MOVEMENT_THRESHOLD_TOUCH)
    ) {
      if (mouseDownTimerRef.current) {
        clearTimeout(mouseDownTimerRef.current);
        mouseDownTimerRef.current = null;
      }

      if (isDragAndDropAllowed && !taskBeingDragged && dragable) {
        initiateDrag("MOVEMENT");
      }
    }
  };

  // Handle click events (drawer opening)
  const handleClick = (isKeyboardActivation = false) => {
    // Only handle click if it was determined to be a click, not a drag
    if (!isKeyboardActivation && !shouldHandleClick) return;

    // Defensive cleanup in case a previous drag leaked
    if (isDragInitiated) return;
    if (taskBeingDragged) return;

    if (onClickOverride) {
      onClickOverride();
      return;
    }

    if (isLockRoutesMode) {
      LockAllRoutesApi.lockTask(dispatch, task.idTask, !task.lockedForPlanning);
      return;
    }
    if (
      highlightedTaskIds.includes(task.idTask) ||
      highlightedTaskIds.length === 0
    ) {
      dispatch(
        openDrawer({
          type: "Task",
          drawerContent: {
            task: task,
            employeeName: employee?.fullName,
          },
        }),
      );
    } else {
      dispatch(setHighlightedTasks([]));
    }

    // Reset click handler flag
    setShouldHandleClick(false);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (mouseDownTimerRef.current) {
        clearTimeout(mouseDownTimerRef.current);
      }
    };
  }, []);

  //set var value for borderColor on bottomBorder.
  const cssVars = {
    "--task-border-color":
      isLockRoutesMode && !task.lockedForPlanning
        ? "var(--col-graph-third-2)"
        : borderColor,
  } as React.CSSProperties;

  return (
    <>
      <div
        style={{
          position: "absolute",
          left: xPosition + 2,
          top: yPosition,
          width,
          height,
        }}
        onMouseEnter={() => {
          // Suppress hover highlighting while a different task is being dragged —
          // the cursor still passes over other tasks mid-drag, which shouldn't light them up.
          if (isDragging) return;
          dispatch(
            showTaskHoverBox({
              type: taskType,
              task: task,
              employee: employee,
            }),
          );
        }}
        onMouseLeave={() => {
          dispatch(hideTaskHoverBox());
        }}
      >
        {isDragging &&
          intersectingIdTaskPlanAndPlacement?.targetTaskId === task.idTask &&
          intersectingIdTaskPlanAndPlacement.isCloserToStart && (
            <DragAndDropIndicator />
          )}
        <div
          id={`${taskType}-${task.idTask}`}
          tabIndex={0}
          role="button"
          aria-label={`${task.description ?? task.client?.description ?? ""}`}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClick(true);
            }
          }}
          onFocus={() => {
            dispatch(
              showTaskHoverBox({
                type: taskType,
                task: task,
                employee: employee,
              }),
            );
          }}
          onBlur={() => {
            dispatch(hideTaskHoverBox());
          }}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
          onClick={() => handleClick()}
          className={`${taskStyles.verticalWorkTask} ${isSelected ? taskStyles.taskSelected : ""} ${isDragging ? taskStyles.dragInProgress : ""}`}
          style={{
            height:
              // intersectingIdTaskPlanAndPlacement?.targetTaskId === task.idTask
              //   ? "calc(100% - 4px)"
              "100%",
            zIndex: 1000,
            // Locked tasks get a distinct color scheme so planners can see what is pinned.
            borderLeft: `4px solid ${isLockRoutesMode && task.lockedForPlanning ? "var(--col-graph-third-2)" : borderColor}`,
            backgroundColor: backgroundColor,
            opacity: HighlightTasks.getOpacity(highlightedTaskIds, task.idTask),
            position: "relative",
            boxSizing: "border-box",
            ...cssVars,
          }}
        >
          {children}
          {isDragging &&
            intersectingIdTaskPlanAndPlacement?.targetTaskId === task.idTask &&
            !intersectingIdTaskPlanAndPlacement.isCloserToStart && (
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  width: "calc(100% + 4px)", //4px to account for borderwidth on task.
                  left: "-4px",
                  right: 0,
                  zIndex: 10,
                }}
              >
                <DragAndDropIndicator />
              </div>
            )}
        </div>
      </div>
      {taskExceedsEndOfShift && violationHeight && (
        /*         TODO: Started moving the logic from both WorkTask and Operational task here. 
 But should go back, since operation is not draggable.*/
        <>
          <TaskViolationBox
            xPosition={xPosition}
            yPosition={yPosition}
            violationHeight={violationHeight}
            height={height}
            width={width}
            shiftEndTime={employee!.shiftEndTime}
            fullTaskViolation={fullTaskViolation}
          />
        </>
      )}
    </>
  );
};

export default DraggableGanttChartBlock;
